/**
 * The mention chip inside the editor — the node view of `CustomMention`:
 * `@label`, blue when the item has parameters and slate when it is a pure
 * reference, plus a minor chip per chosen parameter.
 *
 * Clicking it opens the parameter picker (#58 item 3, the old web's hamburger).
 * Until that landed, `selectedParams` could be rendered and never chosen — the
 * marker format, the minor chips and the send-time enrichment were all in place
 * and the only way to get a parameter onto a chip was to paste one in.
 *
 * Selection lives in `node.attrs.selectedParams` and is written through
 * `updateAttributes`, so the editor's own update cycle fires once and the marker
 * text `getProcessedText` emits stays in step — the same single source of truth
 * the reference uses.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { NodeViewWrapper, type ReactNodeViewProps } from "@tiptap/react";
import { useDocumentContextCtx } from "../../contexts/DocumentContextProvider";
import {
  MENTION_WRAPPER_CLASS,
  MINOR_CHIP_CLASS,
  MINOR_CHIP_OVERFLOW_CLASS,
  getMajorChipClass,
  getMajorChipText,
  getMajorChipTooltip,
  getMinorChipTooltip,
  getOverflowTooltip,
  splitParamsForDisplay,
} from "../../lib/mentionChipPresentation";
import { isSkillMentionType, type MentionParameter } from "../../lib/mentionUtils";
import type { DocumentContextItem } from "../../lib/documentContext";
import { MentionParamPopover } from "./MentionParamPopover";

export function MentionChipNode({ node, updateAttributes }: ReactNodeViewProps) {
  const { extractable, item: lookupItem } = useDocumentContextCtx();
  const type = String(node.attrs["type"] ?? "");
  const id = String(node.attrs["id"] ?? "");
  const label = String(node.attrs["label"] ?? "");
  const params: MentionParameter[] = Array.isArray(node.attrs["selectedParams"]) ? node.attrs["selectedParams"] : [];
  const probe = isSkillMentionType(type) ? false : extractable(type, id);
  const { visible, overflow } = splitParamsForDisplay(params);

  const chipRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState<{ top: number; bottom: number; left: number } | null>(null);
  const [item, setItem] = useState<DocumentContextItem | null>(null);
  const [loading, setLoading] = useState(false);

  // A skill mention names a folder, not an element — there are no parameters to
  // choose, so it stays a plain chip.
  const pickable = !isSkillMentionType(type);

  useEffect(() => {
    if (!open || !pickable || !lookupItem) return;
    let live = true;
    setLoading(true);
    void Promise.resolve(lookupItem(type, id))
      .then((found) => {
        if (live) setItem(found);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [open, pickable, lookupItem, type, id]);

  const openPicker = useCallback(() => {
    if (!pickable) return;
    const rect = chipRef.current?.getBoundingClientRect();
    if (rect) setAnchorRect({ top: rect.top, bottom: rect.bottom, left: rect.left });
    setOpen(true);
  }, [pickable]);

  const onChange = useCallback(
    (next: MentionParameter[]) => {
      updateAttributes({ selectedParams: next });
    },
    [updateAttributes],
  );

  return (
    <NodeViewWrapper
      as="span"
      className={MENTION_WRAPPER_CLASS}
      data-type={type}
      data-id={id}
      data-label={label}
      data-drag-handle=""
      contentEditable={false}
    >
      <span
        ref={chipRef}
        className={getMajorChipClass(probe ?? undefined)}
        title={getMajorChipTooltip(type, id)}
        // A button would be the honest element, but this sits inside a
        // contenteditable and a nested button steals the caret on focus. The
        // role and key handler give it the same affordances without that.
        {...(pickable
          ? {
              role: "button" as const,
              tabIndex: 0,
              // The editor moves the caret on mousedown; preventing the default
              // keeps the selection where the user left it while the panel opens.
              onMouseDown: (e: React.MouseEvent) => e.preventDefault(),
              onClick: openPicker,
              onKeyDown: (e: React.KeyboardEvent) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openPicker();
                }
              },
            }
          : {})}
      >
        <span className="revit-mention-text">{getMajorChipText(label)}</span>
      </span>
      {visible.map((param) => (
        <span key={`${param.id}-${param.name}`} className={MINOR_CHIP_CLASS} title={getMinorChipTooltip(param)} data-param-id={param.id}>
          <span className="revit-mention-text">{param.name}</span>
        </span>
      ))}
      {overflow.length > 0 ? (
        <span className={MINOR_CHIP_OVERFLOW_CLASS} title={getOverflowTooltip(overflow)}>
          +{overflow.length}
        </span>
      ) : null}
      {open && pickable ? (
        <MentionParamPopover
          item={item}
          loading={loading}
          label={label}
          selected={params}
          onChange={onChange}
          onClose={() => setOpen(false)}
          anchorRect={anchorRect}
        />
      ) : null}
    </NodeViewWrapper>
  );
}
